import React, { useEffect, useMemo, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import { CreditCard, Banknote, Building2, Tag } from 'lucide-react'
import Modal from './Modal'
import { cn } from '../lib/utils'

// R1.5 — Record-payment modal. Card / cash / EFT / Discount, full or partial.
//
// Two modes:
//  • INVOICE mode  (pass `invoice`) — records a payment against one invoice,
//    defaults to the outstanding balance so "pay it off" is one click.
//  • ACCOUNT mode  (pass `account`) — takes a payment at the account level even
//    when it isn't tied to an invoice yet. By default it settles the oldest open
//    invoices first and banks the remainder as credit; untick "Allocate…" to take
//    it purely as an unallocated deposit/credit (e.g. a whitening deposit).
//
// Every payment now carries a DATE the money was received (defaults to today)
// because deposits are often taken before the work / invoice exists.

const METHODS = [
  { value: 'card',     label: 'Card',     icon: CreditCard, hint: 'Yoco / Zapper tap' },
  { value: 'cash',     label: 'Cash',     icon: Banknote,   hint: 'Counted at reception' },
  { value: 'eft',      label: 'EFT',      icon: Building2,  hint: 'Bank transfer' },
  { value: 'discount', label: 'Discount', icon: Tag,        hint: 'Practice write-down' },
]

const todayISO = () => {
  const d = new Date()
  const off = d.getTimezoneOffset()
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10)
}
// Floor the back-date picker 5 years back — matches the server guard; stops fat-fingered years.
const minISO = () => {
  const d = new Date(); d.setFullYear(d.getFullYear() - 5)
  const off = d.getTimezoneOffset()
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10)
}

// DISCREASON (29 Oct 2026): mirror of Invoice.clean_discount_reason (min 3 visible characters), a little stricter
// on the client: strip whitespace (incl. NBSP / line separators), every Unicode format character (\p{Cf}: zero-width,
// LRM/RLM, U+2063 ...), the blank-looking fillers (Hangul U+3164 U+115F U+1160, halfwidth U+FFA0, braille blank
// U+2800, CGJ U+034F) and the variation selectors U+FE00-FE0F, then count GRAPHEME CLUSTERS (two emoji = 2).
const REASON_INVISIBLE = /[\s\p{Cf}\u3164\u115F\u1160\uFFA0\u2800\u034F\uFE00-\uFE0F]/gu
export const MIN_DISCOUNT_REASON = 3
const graphemeCount = (str) => {
  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    let n = 0
    for (const _ of new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(str)) n += 1
    return n
  }
  // Old browsers: NFC-normalise, drop combining marks (\p{M}), count code points.
  return Array.from(str.normalize('NFC').replace(/\p{M}/gu, '')).length
}
export const discountReasonOk = (raw) =>
  graphemeCount(String(raw ?? '').replace(REASON_INVISIBLE, '')) >= MIN_DISCOUNT_REASON

// onError gives { field: message }; a message can be a string, an array or a nested object. Always return one
// readable string: nested parts joined with "; " (never "[object Object]", never "a,b").
export const flattenErrors = (v) => {
  if (v == null) return ''
  if (typeof v === 'string') return v
  if (Array.isArray(v)) return v.map(flattenErrors).filter(Boolean).join('; ')
  if (typeof v === 'object') return Object.values(v).map(flattenErrors).filter(Boolean).join('; ')
  return String(v)
}

export const REFUSED_FALLBACK = 'the server refused this payment, please check and try again'
// Classify ONE Inertia response. `page` is the page object Inertia hands to onSuccess for THIS request, never a
// usePage() snapshot taken before submit. Rails flash is set on the POST and rendered on the redirect GET that
// answers it, then consumed, so page.props.flash belongs to this response alone; an older snapshot could be
// stale. ApplicationController shares flash as { notice: nil, alert: nil }, so "alert present" means
// non-null (an empty string "" IS present and is a refusal).
//   ok       -> alert absent
//   refused  -> alert present, no notice (empty alert gets a fallback message). Nothing was recorded.
//   warning  -> alert AND notice present. The controllers never set a notice on a refusal, so the payment WAS
//               recorded: close the modal (a retry would record it twice) and warn.
export const classifyPaymentResponse = (page) => {
  const f = page?.props?.flash || {}
  const hasAlert = f.alert !== null && f.alert !== undefined
  const notice = f.notice == null ? '' : String(f.notice)
  if (!hasAlert) return { kind: 'ok', notice }
  const alert = String(f.alert).trim()
  if (notice.trim()) return { kind: 'warning', text: `Payment recorded, but the server also said: ${alert || 'there was a warning'}. Please check the invoice.` }
  return { kind: 'refused', text: `Not recorded: ${alert || REFUSED_FALLBACK}` }
}

export default function PaymentModal({ open, onClose, invoice = null, account = null, onSaved = null }) {
  const isAccount = !!(account && account.id) && !(invoice && invoice.id)
  const balance = isAccount
    ? Math.max(0, Number(account?.outstanding || 0))
    : Math.max(0, Number(invoice?.balance || 0))

  const [amount, setAmount]       = useState('')
  const [method, setMethod]       = useState('card')
  const [reference, setReference] = useState('')
  const [notes, setNotes]         = useState('')
  const [reason, setReason]       = useState('') // writeoffdiscount1028: a discount needs a reason
  const [receivedAt, setReceivedAt] = useState(todayISO())
  const [allocate, setAllocate]   = useState(true) // account mode only
  const [saving, setSaving]       = useState(false)
  const [amountError, setAmountError] = useState('')
  const [serverError, setServerError] = useState(null) // DISCREASON: { tone: 'red'|'amber', text } shown inside the modal
  const submittingRef = useRef(false) // DISCREASON: synchronous double-submit lock (button AND Enter)
  const amountRef = useRef(null)

  useEffect(() => {
    if (!open) return
    // INVOICE payments: DO NOT auto-populate the amount — reception must type it in manually as an
    // accuracy double-check, so a wrong/partial amount can't be banked on autopilot (Paul 2026-06-26).
    // Account-level payments keep the balance prefill (those are usually a full settlement).
    setAmount(isAccount && balance > 0 ? balance.toFixed(2) : '')
    setMethod('card'); setReference(''); setNotes(''); setReason(''); setReceivedAt(todayISO())
    setAllocate(true); setSaving(false); setAmountError(''); setServerError(null)
    setTimeout(() => { amountRef.current?.focus(); amountRef.current?.select() }, 0)
  }, [open, balance])

  // A visit dropped before it was sent must never leave Record dead: release the lock whenever the modal opens or closes.
  useEffect(() => { submittingRef.current = false }, [open])

  const overpay = useMemo(() => {
    const a = parseFloat(amount)
    return balance > 0 && a > balance ? a - balance : 0
  }, [amount, balance])

  const reasonMissing = method === 'discount' && !discountReasonOk(reason)

  const submit = () => {
    if (submittingRef.current) return // two Enters / a double click = exactly one POST
    if (reasonMissing) { setServerError({ tone: 'red', text: 'A discount needs a reason (at least 3 characters).' }); return }
    const amt = parseFloat(amount)
    if (!amt || amt <= 0) {
      setAmountError('Enter an amount greater than R0')
      amountRef.current?.focus()
      return
    }
    setAmountError('')
    setServerError(null) // clear the previous outcome at the start of each submit
    submittingRef.current = true
    setSaving(true)

    const done = (defaultMsg) => ({
      // preserveState: Inertia's default for a non-GET with no validation errors re-creates the page component
      // after the 303, which resets the page's payOpen and wipes this banner. Keep it so a refusal stays visible.
      preserveState: true,
      preserveScroll: true,
      onSuccess: (page) => {
        // The server refuses with a 303 + flash alert, which Inertia reports as a "success". Classify THIS
        // response's page (see classifyPaymentResponse). A refusal shows the banner, keeps the modal open, no toast.
        const out = classifyPaymentResponse(page)
        if (out.kind === 'refused') {
          setServerError({ tone: 'red', text: out.text })
          return // nothing was recorded: no onSaved, the modal stays open for a correction
        }
        if (out.kind === 'warning') {
          // Recorded, with a server warning: never leave a live Record button (a retry would double-record).
          if (typeof toast.warning === 'function') toast.warning(out.text); else toast(out.text)
          onSaved?.()
          onClose?.()
          return
        }
        toast.success(out.notice || defaultMsg)
        // Flag the owning patient's page as stale so its money cards re-sync silently on return.
        onSaved?.()
        onClose?.()
      },
      onError: (errs) => {
        const msg = flattenErrors(errs) || 'Could not record'
        setServerError({ tone: 'red', text: `Not recorded: ${msg}` })
      },
      onCancel: () => { submittingRef.current = false; setSaving(false) },
      onFinish: () => { submittingRef.current = false; setSaving(false) },
    })

    try {
      if (isAccount) {
        // Allocate → settle oldest invoices first, remainder to credit.
        // Don't allocate → bank the whole amount as an unallocated account credit (deposit).
        const url = allocate
          ? `/accounts/${account.id}/receive_payment`
          : `/accounts/${account.id}/deposit`
        router.post(url, {
          amount: amt, method, received_at: receivedAt,
          reference: reference || null, notes: notes || null,
          reason: method === 'discount' ? (reason || null) : null,
        }, done('Payment recorded'))
      } else {
        router.post(`/invoices/${invoice.id}/payments`, {
          amount: amt, method, received_at: receivedAt,
          reference: reference || null, notes: notes || null,
          reason: method === 'discount' ? (reason || null) : null,
        }, done('Payment recorded'))
      }
    } catch (e) {
      submittingRef.current = false; setSaving(false)
      setServerError({ tone: 'red', text: 'Not recorded: could not send the payment, please try again' })
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isAccount ? 'Add payment to account' : 'Record payment'}
      size="md"
      footer={
        <>
          <button type="button" onClick={onClose}
            className="rounded-2xl px-4 py-2 text-sm font-medium text-brand-muted hover:bg-brand-surface/45 hover:text-brand-ink">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={saving || reasonMissing}
            title={reasonMissing ? 'Enter a discount reason (at least 3 characters)' : undefined}
            className="rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
            {isAccount ? 'Add payment' : 'Record payment'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {serverError && (
          <div role="alert" className={serverError.tone === 'amber'
            ? 'rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800'
            : 'rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700'}>
            {serverError.text}
          </div>
        )}
        {isAccount ? (
          <div className="rounded-xl border border-brand-border bg-brand-surface/40 px-3 py-2 text-sm text-brand-ink">
            {balance > 0
              ? <>Account outstanding: <strong>R{balance.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</strong></>
              : <>No outstanding invoices — this will be banked as account credit.</>}
          </div>
        ) : balance > 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Outstanding balance: <strong>R{balance.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</strong>
          </div>
        ) : (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            Invoice already paid in full. You can still record a refund/credit if needed.
          </div>
        )}

        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">Method</label>
          <div className="grid grid-cols-4 gap-2">
            {METHODS.map((m) => {
              const Icon = m.icon
              const active = method === m.value
              return (
                <button key={m.value} type="button" onClick={() => { setMethod(m.value); setServerError(null) }}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-sm font-medium transition-colors',
                    active
                      ? 'border-brand-primary bg-brand-primary/5 text-brand-primary ring-2 ring-brand-primary/30'
                      : 'border-brand-border bg-white text-brand-ink hover:bg-brand-surface'
                  )}>
                  <Icon size={20} />
                  {m.label}
                  <span className="text-[10px] leading-tight text-brand-muted">{m.hint}</span>
                </button>
              )
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">Amount (R)</label>
            <input ref={amountRef} type="number" step="0.01" value={amount}
              onChange={(e) => { setAmount(e.target.value); if (amountError) setAmountError('') }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
              className={`w-full rounded-2xl border bg-white px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:ring-4 ${amountError ? 'border-red-400 focus:ring-red-200' : 'border-brand-accent/80 focus:border-brand-primary focus:ring-brand-accent/45'}`} />
            {amountError && <p className="mt-1 text-xs text-red-600">{amountError}</p>}
            {overpay > 0 && (
              <p className="mt-1 text-xs text-amber-700">R{overpay.toLocaleString('en-ZA', { minimumFractionDigits: 2 })} over the balance — recorded as a patient credit.</p>
            )}
            {balance > 0 && (
              <button type="button" onClick={() => { setAmount(balance.toFixed(2)); setAmountError('') }}
                className="mt-1 text-xs text-brand-primary hover:underline">
                Pay full balance (R{balance.toFixed(2)})
              </button>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">Date received</label>
            <input type="date" value={receivedAt} max={todayISO()} min={minISO()}
              onChange={(e) => setReceivedAt(e.target.value)}
              className="w-full rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45" />
          </div>
        </div>

        {method === 'discount' && (
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">Discount reason <span className="text-red-600" aria-hidden="true">*</span></label>
            <input type="text" value={reason} onChange={(e) => { setReason(e.target.value); if (serverError) setServerError(null) }}
              placeholder="e.g. pensioner, loyalty, goodwill" aria-required="true"
              className={`w-full rounded-2xl border bg-white px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:ring-4 ${reasonMissing ? 'border-red-400 focus:ring-red-200' : 'border-brand-accent/80 focus:border-brand-primary focus:ring-brand-accent/45'}`} />
            <p className={`mt-1 text-xs ${reasonMissing ? 'text-red-600' : 'text-brand-muted'}`}>Required: at least 3 characters</p>
          </div>
        )}

        {method === 'eft' && (
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">EFT reference</label>
            <input type="text" value={reference} onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. POP-1234"
              className="w-full rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45" />
          </div>
        )}

        {isAccount && (
          <label className="flex items-start gap-2.5 rounded-xl border border-brand-border bg-white px-3 py-2.5 text-sm text-brand-ink">
            <input type="checkbox" checked={allocate} onChange={(e) => setAllocate(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-brand-border text-brand-primary focus:ring-brand-primary" />
            <span>
              <span className="font-medium">Allocate to outstanding invoices</span>
              <span className="block text-xs text-brand-muted">
                {allocate
                  ? 'Settles the oldest open invoices first; any remainder is banked as account credit.'
                  : 'Take it regardless of an invoice — the full amount is banked as an unallocated account credit (e.g. a deposit).'}
              </span>
            </span>
          </label>
        )}

        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">Notes (optional)</label>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            className="w-full resize-none rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45" />
        </div>
      </div>
    </Modal>
  )
}
